// 旧路径集中重定向表（P2 菜单合并 65→41 配套 · 301 兼容层）
// 三类来源：
//   A. 仓库早期扁平路由（首屏旧书签，原 App.tsx L254-290 收敛）
//   B. 平台内既有旧路径（历史挪位/改名，原 App.tsx 内散落的 <Navigate> 收敛）
//   C. 本次被合并叶旧路径 → 对应 Tab 壳路径（对照 menu.tsx 旧清单逐条列出）
// 全部 replace 导航（不压历史）；App.tsx 在 <Routes> 内渲染本表导出的 legacyRedirectRoutes。
import { Navigate, Route } from 'react-router-dom';

interface LegacyRedirect {
  /** 旧路径（不含前导斜杠，对齐 <Route path> 相对写法） */
  from: string;
  /** 新路径（壳路径带 ?tab= 时指向具体 Tab） */
  to: string;
}

/**
 * A. 早期扁平路由（/overview 等 → /platform/*）。
 * 目标落点已按本次菜单合并更新为壳路径（带 ?tab=）。
 */
const FLAT_ROUTES: LegacyRedirect[] = [
  { from: 'overview', to: '/platform' },
  { from: 'notifications', to: '/platform/notifications' },
  { from: 'audit-workbench', to: '/platform/account/audit/agent-onboarding' },
  { from: 'agents', to: '/platform/account/agents' },
  { from: 'merchants', to: '/platform/account/merchants' },
  { from: 'channels', to: '/platform/account/upstream-channels?tab=accounts' },
  { from: 'products', to: '/platform/account/products' },
  { from: 'cards', to: '/platform/card/ops' },
  // 以下目标为本次合并壳路径（原指向被合并叶）
  { from: 'realtime-transactions', to: '/platform/card/transactions?tab=stream' },
  { from: 'physical-approval', to: '/platform/card/physical?tab=batches' },
  { from: 'physical-inventory', to: '/platform/card/physical?tab=inventory' },
  { from: 'finance', to: '/platform/finance/overview' },
  { from: 'deposit-review', to: '/platform/finance/deposit-reviews' },
  { from: 'withdrawal-review', to: '/platform/finance/withdrawal-reviews' },
  { from: 'merchant-wallet', to: '/platform/finance/merchant-wallet' },
  { from: 'va-accounts', to: '/platform/finance/va-accounts' },
  { from: 'chain-accounts', to: '/platform/finance/chain-accounts' },
  { from: 'transfers', to: '/platform/finance/settlement?tab=transfer' },
  { from: 'treasury', to: '/platform/finance/treasury-positions' },
  { from: 'reconciliation', to: '/platform/finance/settlement-batches?tab=recon' },
  { from: 'settlement', to: '/platform/finance/settlement-batches?tab=batches' },
  { from: 'escrow', to: '/platform/finance/overview?tab=escrow' },
  { from: 'risk-alerts', to: '/platform/risk/alerts' },
  { from: 'disputes', to: '/platform/risk/kyc-review?tab=disputes' },
  { from: 'compliance', to: '/platform/risk/compliance' },
  { from: 'risk-rules', to: '/platform/risk/rules' },
  { from: 'risk-blocklist', to: '/platform/risk/rules?tab=blocklist' },
  { from: 'kyc-review', to: '/platform/risk/kyc-review' },
  { from: 'aml', to: '/platform/risk/aml' },
  { from: '3ds', to: '/platform/risk/aml?tab=3ds' },
  { from: 'ops-tickets', to: '/platform/ops/service?tab=tickets' },
  { from: 'ops-ai', to: '/platform/ops/service?tab=kb' },
  { from: 'reports', to: '/platform/report/dashboard' },
  { from: 'email-service', to: '/platform/system/email-smtp' },
  { from: 'security', to: '/platform/system/security' },
  { from: 'settings', to: '/platform/system/settings' },
];

/**
 * B. 平台内既有旧路径（历史挪位/改名兼容，原 App.tsx 内散落 <Navigate> 收敛于此）。
 */
const LEGACY_PLATFORM_ROUTES: LegacyRedirect[] = [
  // 通道工作台历史路径
  { from: 'platform/account/channels', to: '/platform/account/upstream-channels?tab=accounts' },
  { from: 'platform/account/channel-mappings', to: '/platform/account/upstream-channels?tab=field-maps' },
  // 划转复核历史路径
  { from: 'platform/finance/credit-reviews', to: '/platform/finance/merchant-credits' },
  { from: 'platform/finance/transfer-reviews', to: '/platform/finance/merchant-credits' },
  // 2026-09-19-N2 S7c：费项目录挪入账户中心产品分区
  { from: 'platform/finance/fee-items', to: '/platform/account/products/fee-items' },
];

/**
 * C. 本次被合并叶旧路径 → Tab 壳路径（对照 menu.tsx 旧清单逐条列出；壳基路径与
 * 旧主页面路径重合者直接挂壳、主 Tab 即原页面，无需重定向，不在此列）。
 */
const MERGED_LEAF_ROUTES: LegacyRedirect[] = [
  // 账户中心：变更与批量审核壳
  { from: 'platform/account/audit/merchant-kyc-change', to: '/platform/account/audit/change-batch?tab=change' },
  { from: 'platform/account/audit/batch', to: '/platform/account/audit/change-batch?tab=batch' },
  // 卡片中心：卡片总览壳
  { from: 'platform/card/dashboard', to: '/platform/card/overview?tab=dashboard' },
  { from: 'platform/card/search', to: '/platform/card/overview?tab=search' },
  // 卡片中心：交易查询壳
  { from: 'platform/card/tx/stream', to: '/platform/card/transactions?tab=stream' },
  { from: 'platform/card/tx/query', to: '/platform/card/transactions?tab=query' },
  // 卡片中心：实体卡壳
  { from: 'platform/card/physical/inventory', to: '/platform/card/physical?tab=inventory' },
  { from: 'platform/card/physical/batches', to: '/platform/card/physical?tab=batches' },
  // 财务中心：资金总览壳
  { from: 'platform/finance/escrow', to: '/platform/finance/overview?tab=escrow' },
  // 财务中心：结算对账壳
  { from: 'platform/finance/merchant-statements', to: '/platform/finance/settlement?tab=merchant' },
  { from: 'platform/finance/agent-spread-settlements', to: '/platform/finance/settlement?tab=agent' },
  { from: 'platform/finance/fund-transfers', to: '/platform/finance/settlement?tab=transfer' },
  // 财务中心：批次与对账壳
  { from: 'platform/finance/reconciliation', to: '/platform/finance/settlement-batches?tab=recon' },
  // 风控合规：风险预警壳
  { from: 'platform/risk/fraud/gangs', to: '/platform/risk/alerts?tab=gangs' },
  // 风控合规：规则与名单壳
  { from: 'platform/risk/blocklist', to: '/platform/risk/rules?tab=blocklist' },
  // 风控合规：KYC 复审壳
  { from: 'platform/risk/kyc-inspect-fines', to: '/platform/risk/kyc-review?tab=fines' },
  { from: 'platform/risk/disputes', to: '/platform/risk/kyc-review?tab=disputes' },
  // 风控合规：AML 与 3DS 壳
  { from: 'platform/risk/3ds', to: '/platform/risk/aml?tab=3ds' },
  // 运营中心：内容与活动壳
  { from: 'platform/ops/campaigns', to: '/platform/ops/content?tab=campaigns' },
  { from: 'platform/ops/cockpit', to: '/platform/ops/content?tab=cockpit' },
  // 运营中心：客户服务壳
  { from: 'platform/ops/cs/kb', to: '/platform/ops/service?tab=kb' },
  { from: 'platform/ops/cs/tickets', to: '/platform/ops/service?tab=tickets' },
  { from: 'platform/ops/email/inbound', to: '/platform/ops/service?tab=email' },
  // 运营中心：运维监控壳
  { from: 'platform/ops/config', to: '/platform/ops/sre?tab=config' },
  { from: 'platform/ops/log-analysis', to: '/platform/ops/sre?tab=logs' },
  { from: 'platform/ops/sre/alerts', to: '/platform/ops/sre?tab=alerts' },
  // 报表中心：报表分析壳
  { from: 'platform/report/templates', to: '/platform/report/dashboard?tab=templates' },
  // 报表中心：订阅与导出壳
  { from: 'platform/report/export', to: '/platform/report/subscriptions?tab=export' },
  // 报表中心：协同治理壳
  { from: 'platform/report/acl', to: '/platform/report/collab?tab=acl' },
  { from: 'platform/report/anomaly', to: '/platform/report/collab?tab=anomaly' },
];

/** 全量旧路径重定向（A + B + C） */
const LEGACY_REDIRECTS: LegacyRedirect[] = [
  ...FLAT_ROUTES,
  ...LEGACY_PLATFORM_ROUTES,
  ...MERGED_LEAF_ROUTES,
];

/** 集中重定向路由（App.tsx 直接置于 <Routes> 内渲染；Fragment 形式可被 Routes 展开） */
export const legacyRedirectRoutes = (
  <>
    {LEGACY_REDIRECTS.map(({ from, to }) => (
      <Route key={from} path={from} element={<Navigate to={to} replace />} />
    ))}
  </>
);
